import React, { useEffect, useMemo, useState } from "react";
import CustomInput from "../../../../common/CustomInput";
import NewToolTip from "../../../../common/New-ToolTip";
import { useDispatch, useSelector } from "react-redux";
import { MedicationClasses } from "./data";
import {
  normalizeDrugName,
  getNormalizedMedicationCandidates,
  getNormalizedLabelCandidates,
} from "../../../../../helpers/amrEducationDrugMatch";

const findBestMatchOptionValue = (options = [], medicationBases = []) => {
  if (!options.length || !medicationBases.length) return null;
  const medCandidates = new Set(medicationBases.filter(Boolean));

  const exact = options.find((option) => {
    const optionCandidates = getNormalizedLabelCandidates(option?.value || option?.label || "");
    return optionCandidates.some((candidate) => medCandidates.has(candidate));
  });
  if (exact) return exact.value;

  const partial = options.find((option) => {
    const optionCandidates = getNormalizedLabelCandidates(option?.value || option?.label || "");
    return optionCandidates.some((optionBase) =>
      medicationBases.some(
        (medBase) => medBase.includes(optionBase) || optionBase.includes(medBase)
      )
    );
  });

  return partial?.value || null;
};

const isMedicationSelectable = (item) => {
  const status = (item?.status || "").toString().toLowerCase().trim();
  if (!status) return true;
  return status !== "inactive" && status !== "discontinued";
};

const DRUG_LABEL_MAX_LENGTH = 28;

const truncateLabel = (value = "", maxLength = DRUG_LABEL_MAX_LENGTH) => {
  const safeValue = String(value || "").trim();
  if (safeValue.length <= maxLength) return safeValue;
  return `${safeValue.slice(0, maxLength - 3)}...`;
};

const resolveSelectedValue = (selectedEntry, options = []) => {
  if (!selectedEntry) return null;

  const entryName = (selectedEntry.name || selectedEntry.value || "").toString();
  const entryBase = normalizeDrugName(entryName);
  const exact = options.find(
    (opt) => opt.value === entryName || normalizeDrugName(opt.value) === entryBase
  );

  if (exact) return exact.value;

  const ci = options.find((opt) => {
    const optionBase = normalizeDrugName(opt.value);
    return optionBase?.includes(entryBase) || entryBase.includes(optionBase);
  });

  return ci ? ci.value : entryName;
};

const getSelectedLabelMeta = (selectedValue, className) => {
  const selectedLabel = selectedValue ? String(selectedValue).trim() : "";
  if (!selectedLabel) {
    return {
      selectedLabel: "",
      selectedDisplayLabel: undefined,
      selectedDrugTooltipId: "",
      showTruncatedTooltip: false,
    };
  }

  const selectedDisplayLabel = truncateLabel(selectedLabel);
  return {
    selectedLabel,
    selectedDisplayLabel,
    selectedDrugTooltipId: `education-drug-selected-${className ?.toString().toLowerCase().replace(/\s+/g, "-")}`,
    showTruncatedTooltip: selectedDisplayLabel !== selectedLabel,
  };
};

const DrugTableUI = ({ saveDrugTableInfo = false, classes = MedicationClasses, theme = undefined, onSelectDrugForMedicationCreate = () => {} }) => {
  const { medications, amrEducation, clinicalServiceObj } = useSelector((state) => state.clinicalService);
  const isChol = theme === "cholesterol";
  const selBg = isChol ? "bg-green-75" : "bg-teal-25";
  const selBorder = isChol ? "border-green-150" : "border-teal-100";
  const lineBg = isChol ? "bg-green-150" : "bg-teal-100";
  const dotBg = isChol ? "bg-green-150" : "bg-teal-100";
  const selText = isChol ? "text-teal-300" : "text-teal-300";
  const dispatch = useDispatch();
  const [filteredMed, setFilteredMed] = useState([]);

  const getOptionText = (option) => (option?.value || option?.label || "").toString().trim();

  // Keep table selection in sync with sidebar medications, with persisted fallback.
  useEffect(() => {
    const assessmentMeds =
      clinicalServiceObj?.initialAssessment?.initialAssessmentForm?.medications?.medications || [];
    const sourceMeds = medications?.length > 0 ? medications : assessmentMeds;

    const medicationBases = (sourceMeds || [])
      .filter(isMedicationSelectable)
      .flatMap((item) =>
        getNormalizedMedicationCandidates(
          item?.medicationName || item?.displayName || item?.name || item?.cardName || ""
        )
      )
      .filter(Boolean);

    if (!medicationBases.length) {
      setFilteredMed(amrEducation?.length ? amrEducation : []);
      return;
    }

    const result = classes
      .map((medClass) => {
        const matchedValue = findBestMatchOptionValue(medClass.options, medicationBases);
        return matchedValue ? { class: medClass, name: matchedValue } : null;
      })
      .filter(Boolean);

    setFilteredMed(result);
  }, [amrEducation, medications, classes, clinicalServiceObj]);

  useEffect(() => {
    if (saveDrugTableInfo && filteredMed.length > 0) {
      dispatch({
        type: "UPDATE_AMR_EDUCATION_LOCAL",
        payload: filteredMed,
      });
    }
  }, [saveDrugTableInfo, filteredMed, dispatch]);

  const filteredMedByClassName = useMemo(() => {
    const map = new Map();
    for (const item of filteredMed) {
      const className = item?.class?.className;
      if (!className || map.has(className)) continue;
      map.set(className, item);
    }
    return map;
  }, [filteredMed]);

  const tableRows = useMemo(
    () =>
      classes.map((record) => {
        const selectedEntry = filteredMedByClassName.get(record.className);
        const isSelected = Boolean(selectedEntry);
        const selectedValue = resolveSelectedValue(selectedEntry, record.options);
        const {
          selectedLabel,
          selectedDisplayLabel,
          selectedDrugTooltipId,
          showTruncatedTooltip,
        } = getSelectedLabelMeta(selectedValue, record.className);

        return {
          record,
          isSelected,
          selectedValue,
          selectedLabel,
          selectedDisplayLabel,
          selectedDrugTooltipId,
          showTruncatedTooltip,
        };
      }),
    [classes, filteredMedByClassName]
  );

  return (
    <>
      <div className="w-full flex flex-col gap-2 px-12">
        {tableRows.map(
          ({
            record,
            isSelected,
            selectedValue,
            selectedLabel,
            selectedDisplayLabel,
            selectedDrugTooltipId,
            showTruncatedTooltip,
          }) => {
          return (
            <div key={record.className} className="flex flex-row items-center">
              <div className={`min-w-[330px] gap-[10px] flex items-center justify-start rounded-[10px] ${isSelected && "font-bold"} text-base text-gray-800 py-[14px] px-4 ${isSelected ? `${selBg} ${selBorder} border-2` : "bg-gray-20"}`}>
                {record.className}
              </div>
              <div className="w-full flex flex-row items-center px-2 space-x-2">
                <div className="flex flex-row w-full items-center">
                  <div className={`w-full h-[2px] ${isSelected ? lineBg : "bg-gray-100"}`}></div>
                  <div className={`w-3 h-3 rounded-full ${isSelected ? dotBg : "bg-gray-100"}`}></div>
                </div>
                <div className={` min-w-max text-center ${isSelected ? `${selText} border ${selBorder} rounded-[10px] ${selBg} px-4 py-[14px]` : "text-gray-400"}`}>
                  <span className="min-w-full text-base font-medium">Works in the <span className={`text-base ${isSelected && "font-bold"}`}>{record.worksIn}</span></span>
                </div>

                <div className="flex flex-row w-full items-center">
                  <div className={`w-full h-[2px] ${isSelected ? lineBg : "bg-gray-100"}`}></div>
                  <div className={`w-3 h-3 rounded-full ${isSelected ? dotBg : "bg-gray-100"}`}></div>
                </div>
              </div>

              <div className={`min-w-[320px] box-border ${isSelected ? `${selBg} ${selBorder} rounded-[10px]` : ''}`}>
                <div className="w-full" data-tooltip-id={selectedDrugTooltipId}>
                  <CustomInput
                    options={record.options}
                    name=""
                    className={`col-span-12 w-full !text-gray-800 bg-transparent`}
                    label=""
                    placeholder={"Drug Name"}
                    type="dropdown"
                    value={selectedValue}
                    selectedDisplayLabel={selectedDisplayLabel}
                    onChange={(e) => {
                      const selectedText = getOptionText(e);
                      if (!selectedText) return;
                      onSelectDrugForMedicationCreate(selectedText);
                    }}
                    inputClassName={`!text-base !capitalize ${isSelected ? `!font-semibold  !bg-transparent !text-gray-800` : "!border-gray-100"}`}
                  />
                  {showTruncatedTooltip && (
                    <NewToolTip
                      id={selectedDrugTooltipId}
                      place="bottom"
                      offset={0}
                      render={() => (
                        <div className="max-w-[450px] p-2 text-sm text-black relative bg-white">
                          {selectedLabel}
                        </div>
                      )}
                    />
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

export default DrugTableUI;
